<template>
  <Card
    :footerDivider="false"
    :title="$t('publicUsernameRetrievalInitialization.general.title')"
  >
    <template v-slot:content>
      <div>
        <p
          v-dompurify-html="
            $t('publicUsernameRetrievalInitialization.general.description')
          "
        ></p>
        <p
          v-if="showErrorResponse"
          class="error"
          v-dompurify-html="$t('publicUsernameRetrievalInitialization.general.error')"
        ></p>
      </div>
      <div>
        <div>
          <BasicTextfield
            id="forgot-username.vsnr"
            :value="vsnr"
            :status="vsnrTextfieldStatus"
            :label="$t('publicUsernameRetrievalInitialization.vsnr.label')"
            :required="true"
            @focus="onVsnrInput"
            @update:input="onVsnrUpdate"
            @keyup.enter="onSubmit"
            @update:error="notifyVsnrFieldTouched"
          >
            <template v-slot:error
              >{{ $t('publicUsernameRetrievalInitialization.vsnr.error') }}
            </template>
            <template v-slot:info>
              <div
                v-dompurify-html="$t('publicUsernameRetrievalInitialization.vsnr.info')"
              ></div>
            </template>
          </BasicTextfield>
        </div>
        <div class="card-row__col1"></div>
      </div>
      <div>
        <div>
          <DateInput
            v-model="birthdate"
            id="forgot-username.birthdate"
            placeholder="TT.MM.JJJJ"
            :status="birthdateTextfieldStatus"
            :label="$t('publicUsernameRetrievalInitialization.birthdate.label')"
            :minValidYear="18"
            :required="true"
            mask="DD.MM.YYYY"
            @focus="onBirthdateInput"
            @keyup.enter="onSubmit"
            @update:invalid-date="notifyErrorDate"
            @update:error="notifyErrorDate"
          >
            <template v-slot:error>{{ birthdateErrorMessage }}</template>
            <template v-slot:info
              >{{ $t('publicUsernameRetrievalInitialization.birthdate.info') }}
            </template>
          </DateInput>
        </div>
        <div class="card-row__col1"></div>
      </div>
    </template>
    <template v-slot:footer>
      <div class="footer-cta footer-cta--left">
        <CustomButton
          @click="onSubmit"
          buttonId="forgot-username.weiter-button"
          trackId="forgotUsername"
          :pendingState="pending"
          :buttonColor="ButtonColor.PRIMARY"
          >{{ $t('publicUsernameRetrievalInitialization.submit.text') }}
        </CustomButton>
        <CustomButton
          @click="cancelLink"
          buttonId="forgot-username.weiter-button"
          :buttonColor="ButtonColor.LINK"
        >
          {{
            loggedIn
              ? $t('publicUsernameRetrievalInitialization.return.loggedIn')
              : $t('publicUsernameRetrievalInitialization.return.loggedOut')
          }}
        </CustomButton>
      </div>
    </template>
  </Card>
</template>
<script setup lang="ts">
import dayjs, { Dayjs } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import {
  BasicTextfield,
  ButtonColor,
  Card,
  CustomButton,
  DateInput,
  InputStatus
} from 'de-zurich-ui-library'
import { navigate } from '@/utils/NavigationUtils'
import { escapeVSNR, isValidVSNR } from '@/utilsTenant/rules'
import { isAuthenticated } from '@/utils/KeycloakUtils'
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  is4xxError,
  is5xxError
} from '@/components/account-management/accountcreation/accountcreationUtils'
import { retrieveUsernames } from '@/stores/accountManagementService'
import { ResponseError } from 'account-api'

const { t } = useI18n()

dayjs.extend(customParseFormat)

const emit = defineEmits<{
  onReceiveNoUsernames: [none: void]
  onReceiveMultipleUsernames: [usernames: string[]]
}>()

function getDateFormatISO8601(): string {
  formattedBirthdate.value = null // reset
  const momentDate: Dayjs = dayjs(birthdate.value, 'DD.MM.YYYY', true)
  if (momentDate.isValid()) {
    const formattedDate = momentDate.format('YYYY-MM-DD')
    formattedBirthdate.value = formattedDate
    return formattedDate
  }
  return ''
}

const vsnr = ref('')
const vsnrTextfieldStatus = ref<InputStatus>(InputStatus.DEFAULT)
const birthdate = ref('')
const formattedBirthdate = ref<string | null>()
const birthdateTextfieldStatus = ref<InputStatus>(InputStatus.DEFAULT)
const birthdateErrorMessage = ref('')
const pending = ref(false)
const showErrorResponse = ref(false)
const usernames = ref()
const loggedIn = ref(false)

onMounted(() => {
  loggedIn.value = isAuthenticated()
})
const onVsnrInput = (): void => {
  vsnrTextfieldStatus.value = InputStatus.DEFAULT
}

const onVsnrUpdate = (val: string): void => {
  vsnr.value = val
}

const onBirthdateInput = (): void => {
  birthdateTextfieldStatus.value = InputStatus.DEFAULT
}

const onSubmit = (): void => {
  validateVSNR()
  validateBirthdate()

  sendGetUsernamesRequest()
}

const cancelLink = (): void => {
  if (loggedIn.value) {
    navigate(globalThis.ENV_CONFIG?.REDIRECTS.PERSONAL_PROFIL_PAGE)
  } else {
    navigate(globalThis.ENV_CONFIG?.REDIRECTS.LOGIN_PAGE)
  }
}

const notifyVsnrFieldTouched = (): void => {
  vsnrTextfieldStatus.value = InputStatus.ERROR
}

const validateVSNR = (): void => {
  vsnrTextfieldStatus.value = isValidVSNR(vsnr.value)
    ? InputStatus.VALID
    : InputStatus.ERROR
}

const validateBirthdate = (): void => {
  if (!birthdate.value || birthdate.value.length < 10) {
    birthdateTextfieldStatus.value = InputStatus.ERROR
    birthdateErrorMessage.value = t(
      'publicUsernameRetrievalInitialization.birthdate.errors.wrongFormat'
    )
  }
}

const notifyErrorDate = (): void => {
  birthdateTextfieldStatus.value = InputStatus.ERROR
  birthdateErrorMessage.value = t(
    'publicUsernameRetrievalInitialization.birthdate.errors.wrongFormat'
  )
}

const sendGetUsernamesRequest = async (): Promise<void> => {
  pending.value = true
  formattedBirthdate.value = getDateFormatISO8601()
  const birthdateValue: string = formattedBirthdate.value || birthdate.value
  const escapedVSNR: string = escapeVSNR(vsnr.value)

  try {
    const result = await retrieveUsernames(escapedVSNR, birthdateValue)
    // Ergebnis ist direkt string[]
    handleSuccessfulResponse(result)
  } catch (e) {
    if (e instanceof ResponseError) {
      const status = e.response.status
      if (is5xxError(status)) {
        navigate(globalThis.ENV_CONFIG?.REDIRECTS.TECHNICAL_ERROR_PAGE)
      } else if (is4xxError(status)) {
        showErrorResponse.value = true
      }
    } else {
      // Nicht-ResponseError
      navigate(globalThis.ENV_CONFIG?.REDIRECTS.TECHNICAL_ERROR_PAGE)
    }
  } finally {
    pending.value = false
  }
}

const handleSuccessfulResponse = (usernames_result: string[]): void => {
  pending.value = false
  usernames.value = usernames_result
  if (usernames.value.length > 0) {
    emit('onReceiveMultipleUsernames', usernames.value)
  } else {
    emit('onReceiveNoUsernames')
  }
}
</script>
<style lang="scss" scoped>
span.error-message {
  font-size: 24px;
  color: var(--input-color-error);
}

b.error-message {
  padding-left: 5px;
  color: var(--input-color-error);
}
</style>
